import type { components } from '@/util/apex-client-gen';
import type { MakeDepositsRequest, MakeWithdrawalsRequest } from '@/util/transfer-util';

type ContributionConstraints = components['schemas']['ContributionConstraints'];
type DistributionConstraints = components['schemas']['DistributionConstraints'];
type ContributionType = NonNullable<
  components['schemas']['ContributionConstraintsContributionTypeInfo']['type']
>;
type DistributionType = NonNullable<
  components['schemas']['DistributionConstraintsDistributionTypeInfo']['type']
>;

/** Apex's IRA rules for one account, loaded with its financial details. */
export type IraTransferRules = {
  contribution?: ContributionConstraints | null;
  distribution?: DistributionConstraints | null;
  loading: boolean;
};

export type IraTransferInput = {
  contributionYear?: number;
  contributionType?: ContributionType;
  distributionType?: DistributionType;
  /** Dollar amounts as typed; empty means none. */
  federalWithholding: string;
  stateWithholding: string;
  stateWaiver: boolean;
};

export const emptyIraInput = (): IraTransferInput => ({
  federalWithholding: '',
  stateWithholding: '',
  stateWaiver: false,
});

/** "ROLLOVER_60_DAY" → "Rollover 60 day". */
export const iraTypeLabel = (type: string): string => {
  const words = type.toLowerCase().split('_').filter(Boolean);
  return words.length === 0
    ? type
    : [words[0][0].toUpperCase() + words[0].slice(1), ...words.slice(1)].join(' ');
};

export const contributionTypes = (rules?: IraTransferRules | null): ContributionType[] =>
  (rules?.contribution?.valid_types ?? [])
    .map((item) => item.type)
    .filter((type): type is ContributionType => Boolean(type) && type !== 'TYPE_UNSPECIFIED');

export const distributionTypes = (rules?: IraTransferRules | null): DistributionType[] =>
  (rules?.distribution?.valid_types ?? [])
    .map((item) => item.type)
    .filter((type): type is DistributionType => Boolean(type) && type !== 'TYPE_UNSPECIFIED');

/**
 * Tax years a contribution may go to: the current year, plus last year when
 * the account and the chosen type allow a prior-year contribution.
 */
export const contributionYears = (
  rules: IraTransferRules | null | undefined,
  type: ContributionType | undefined,
  now: Date = new Date(),
): number[] => {
  const year = now.getFullYear();
  const typeInfo = rules?.contribution?.valid_types?.find((item) => item.type === type);
  return rules?.contribution?.only_allow_current_year || !typeInfo?.previous_year_allowed
    ? [year]
    : [year, year - 1];
};

/** Which withholdings Apex accepts for the chosen distribution type. */
export const withholdingOptions = (
  rules: IraTransferRules | null | undefined,
  type: DistributionType | undefined,
) => {
  const typeInfo = rules?.distribution?.valid_types?.find((item) => item.type === type);
  return {
    federal: Boolean(typeInfo?.federal_withholding_enabled),
    state: Boolean(typeInfo?.state_withholding_enabled),
  };
};

const parseDollars = (raw: string): number | null => {
  if (!raw.trim()) return 0;
  const value = Number(raw);
  return Number.isFinite(value) && value >= 0 ? value : null;
};

/** What still has to be filled in before an IRA transfer can be reviewed. */
export const iraInputError = (
  mode: 'deposit' | 'withdraw',
  rules: IraTransferRules,
  input: IraTransferInput,
  amount: number,
): string | null => {
  if (rules.loading) {
    return 'Loading IRA options. Try again in a moment.';
  }
  if (mode === 'deposit') {
    if (!input.contributionType) return 'Choose a contribution type.';
    if (!input.contributionYear) return 'Choose a contribution year.';
    return null;
  }
  if (!input.distributionType) return 'Choose a distribution type.';
  const options = withholdingOptions(rules, input.distributionType);
  const federal = options.federal ? parseDollars(input.federalWithholding) : 0;
  const state = options.state ? parseDollars(input.stateWithholding) : 0;
  if (federal == null || state == null) return 'Enter withholding as a dollar amount.';
  // Whole cents, so 0.10 + 0.20 on a 0.30 withdrawal isn't rejected by float error.
  const cents = (value: number) => Math.round(value * 100);
  if (cents(federal) + cents(state) > cents(amount)) {
    return 'Withholding can’t be more than the withdrawal.';
  }
  return null;
};

const money = (value: number) => ({ amount: { value: value.toFixed(2) } });

export const iraDepositFields = (
  input: IraTransferInput,
): Pick<MakeDepositsRequest, 'ira_contribution'> =>
  input.contributionType && input.contributionYear
    ? { ira_contribution: { tax_year: input.contributionYear, type: input.contributionType } }
    : {};

export const iraWithdrawFields = (
  rules: IraTransferRules,
  input: IraTransferInput,
): Pick<MakeWithdrawalsRequest, 'ira_distribution'> => {
  if (!input.distributionType) return {};
  const options = withholdingOptions(rules, input.distributionType);
  const federal = options.federal ? (parseDollars(input.federalWithholding) ?? 0) : 0;
  const state = options.state ? (parseDollars(input.stateWithholding) ?? 0) : 0;
  return {
    ira_distribution: {
      type: input.distributionType,
      ...(federal > 0 ? { federal_tax_withholding: money(federal) } : {}),
      ...(state > 0 ? { state_tax_withholding: money(state) } : {}),
      ...(options.state ? { state_withholding_waiver: input.stateWaiver } : {}),
    },
  };
};
